import * as THREE from './three.module.min.js';

const container = document.getElementById('three-stage');

/* Background image */
const IMAGE_URL = container.dataset.heroImage;

/* Renderer */
const renderer = new THREE.WebGLRenderer({
  alpha: true,
  antialias: false,
});

renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.25));
renderer.setSize(container.clientWidth, container.clientHeight);

renderer.domElement.style.position      = 'absolute';
renderer.domElement.style.inset         = '0';
renderer.domElement.style.width         = '100%';
renderer.domElement.style.height        = '100%';
renderer.domElement.style.pointerEvents = 'none';

container.appendChild(renderer.domElement);

/* Scene / Camera */
const scene  = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(45, container.clientWidth / container.clientHeight, 0.1, 100);

camera.position.z = 5;

/* Texture */
const loader = new THREE.TextureLoader();
loader.load(IMAGE_URL, (texture) => {
  texture.minFilter = THREE.LinearFilter;
  /* Viewport size */
  const distance   = camera.position.z;
  const fov        = camera.fov * (Math.PI / 180);
  const viewHeight = 2 * Math.tan(fov / 2) * distance;
  const viewWidth  = viewHeight * camera.aspect;

  /* Geometry */
  const geometry = new THREE.PlaneGeometry(viewWidth, viewHeight, 1, 1);

  /* Material */
  const material = new THREE.ShaderMaterial({
    transparent: true,
    uniforms: {
      uTime             : { value: 0, },
      uWarp             : { value: 0.0, },
      uTexture          : { value: texture, },
      uResolution       : { value: new THREE.Vector2(container.clientWidth, container.clientHeight), },
      uNoiseStrength    : { value: 0, },
      uImageResolution  : { value: new THREE.Vector2(texture.image.width, texture.image.height), },
      uParticleStrength : { value: 0.0, },
    },

    vertexShader: `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,

    fragmentShader: `
      uniform sampler2D uTexture;
      uniform vec2 uResolution;
      uniform vec2 uImageResolution;
      uniform float uTime;
      uniform float uWarp;
      uniform float uNoiseStrength;
      uniform float uParticleStrength;

      varying vec2 vUv;

      vec2 coverUv(vec2 uv, vec2 screenSize, vec2 imageSize) {
        float screenRatio = screenSize.x / screenSize.y;
        float imageRatio  = imageSize.x / imageSize.y;
        vec2 newUv = uv;
        if (screenRatio < imageRatio) {
          float scale = screenRatio / imageRatio;
          newUv.x     = uv.x * scale + (1.0 - scale) * 0.5;
        } else {
          float scale = imageRatio / screenRatio;
          newUv.y     = uv.y * scale + (1.0 - scale) * 0.5;
        }
        return newUv;
      }

      /* random */
      float random(vec2 st) {
        return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
      }

      void main() {
        vec2 uv = coverUv( vUv, uResolution, uImageResolution ); 
        /* =========================
           warp（形の歪み）
        ========================= */
        uv.y += uWarp;

        /* =========================
           RGB shift
        ========================= */

        float noise1     = random(vec2(uTime * 0.0008, vUv.y));  
        float noise2     = random(vec2(uTime * 0.0012, vUv.x));  
        float microShift = ((noise1 - 0.5) + (noise2 - 0.5)) * 0.004; 

        vec2 shift = vec2(microShift, 0.0);
        
        float r = texture2D(uTexture, uv + shift).r;
        float g = texture2D(uTexture, uv).g;
        float b = texture2D(uTexture, uv - shift).b;
        
        vec4 color = vec4(r, g, b, 1.0);

        /* =========================
           noise（粒）
        ========================= */

        float noiseX = random( floor(gl_FragCoord.xy) + floor(uTime * 0.04) ); 
        float noiseY = random( floor(gl_FragCoord.yx) + floor(uTime * 0.02) ); 

        float scanNoise     = (noiseX * 0.7 + noiseY * 0.3);
        float particleNoise = random(gl_FragCoord.xy * fract(uTime * 0.1)) * uParticleStrength;
        
        float noise = mix(scanNoise, particleNoise, 0.25); 
        noise = (noise - 0.5) * uNoiseStrength; 
        
        color.rgb += noise;

        gl_FragColor = vec4(color.rgb, 0.61);
      }
    `,
  });

  /* Mesh */
  const mesh = new THREE.Mesh(
    geometry,
    material
  );

  scene.add(mesh);

  /* Scroll inertia */
  let currentScroll = window.scrollY;
  let targetScroll  = window.scrollY;

  

  window.addEventListener(
    'scroll',
    () => {

      targetScroll = window.scrollY;

    },
    { passive: true }
  );

  /* Animation */
  function animate() {
    requestAnimationFrame(animate);

    /* Time */
    material.uniforms.uTime.value = performance.now() * 0.001;;

    /* Scroll delta */
    const delta = targetScroll - currentScroll;
    currentScroll += delta * 0.08;

    /* Scroll velocity */
    const velocity = Math.abs(delta);

    /* =========================
       Noise strength (existing)
    ========================= */
    const targetNoiseStrength = Math.min(velocity * 0.025, 0.06);

    material.uniforms.uNoiseStrength.value += (targetNoiseStrength - material.uniforms.uNoiseStrength.value) * 0.08;

    const targetWarp = Math.sin(performance.now() * 0.001 + velocity * 20.0) * Math.min(velocity * 0.0003, 0.003);

    material.uniforms.uWarp.value += (targetWarp - material.uniforms.uWarp.value) * 0.08;

    const targetParticleStrength = Math.min(velocity * 0.02, 0.4);

    material.uniforms.uParticleStrength.value += (targetParticleStrength - material.uniforms.uParticleStrength.value) * 0.08;  
    renderer.render(scene, camera);
  }

  animate();

  /* Resize */
  window.addEventListener('resize', () => {
    const width  = container.clientWidth;
    const height = container.clientHeight;

    /* Renderer */
    renderer.setSize(width, height);

    /* Camera */
    camera.aspect = width / height;
    camera.updateProjectionMatrix();

    /* Uniform */
    material.uniforms.uResolution.value.set(width, height);

    /* Viewport size */
    const distance = camera.position.z;
    const fov = camera.fov * (Math.PI / 180);
    const viewHeight = 2 * Math.tan(fov / 2) * distance;
    const viewWidth = viewHeight * camera.aspect;

    /* Geometry rebuild */
    mesh.geometry.dispose();
    mesh.geometry = new THREE.PlaneGeometry(viewWidth, viewHeight, 1, 1);
  });

});